import type { SearchFeedParams, FeedDto } from '@/api/feed';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import CommentActionSheet from '@/screen/feed/view/components/comment-action-sheet';
import { useFeedCommentEvent } from '@/hooks/use-feed-comment-event';
import { useFeedLikeEvent } from '@/hooks/use-feed-like-event';
import FeedItem from '@/components/complex/feed-item';
import api from '@/api';
import { useAsyncEffect } from '@/hooks/use-async-effect';
import { useAuth } from '@/components/context/auth-context';

import SkeletonFeedItem from '@/components/complex/skeleton-feed-item';
import FeedMasonry, { FeedMasonryItem } from '@/components/complex/feed-masonry';
import Button from '@/components/primitives/button';
import Typography from '@/components/primitives/typography';
import { PC_MEDIA_QUERY } from '@/libs/config/responsive';
import { saveFeedListSnapshot, takeFeedListSnapshot } from '@/libs/navigation/feed-list-restoration';
import styles from './styles.module.scss';

const LOADING_SKELETON_COUNT = 3;

// 뒤로 가기 복원 때 목록을 구분하는 키. 서버가 준 첫 목록은 캐시 갱신으로 바뀔 수 있어 넣지 않는다.
const buildRestorationKey = (props: FeedItemsProps) => JSON.stringify([
  window.location.pathname,
  window.location.search,
  props.keyword,
  props.accountName,
  props.hashTag,
  props.location,
  props.distance,
  props.size,
  props.link,
  props.sortType,
  props.infiniteScrollType,
]);

export interface FeedItemsProps extends SearchFeedParams {
  feeds?: FeedDto[];
  className?: string;
  onTotalElementsChange?: (totalElements: number) => void;
  onEmptyResult?: () => React.ReactNode;
  infiniteScrollType?: 'page' | 'beforeCreatedAt';
}

export default function FeedItems(props: FeedItemsProps) {
  const {
    feeds: feedsProp = [],
    className = '',
    onTotalElementsChange,
    onEmptyResult,
    keyword,
    accountName,
    hashTag,
    location,
    distance,
    page: pageProp,
    size = 10,
    link,
    sortType,
    infiniteScrollType = 'beforeCreatedAt',
  } = props;
  const paramsArray = [
    feedsProp?.map(a => a.sid).join(','),
    keyword,
    accountName,
    hashTag,
    location,
    distance,
    pageProp,
    size,
    JSON.stringify(link),
    sortType,
    props.beforeCreatedAt,
    infiniteScrollType,
  ];
  const [feeds, setFeeds] = useState<FeedDto[]>(feedsProp);
  const [beforeCreatedAt, setBeforeCreatedAt] = useState<string>();
  const [page, setPage] = useState<number>(pageProp || 0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // 자동 추가 때 초점은 옮기지 않고 추가 건수만 스크린리더에 알린다.
  const [announcement, setAnnouncement] = useState('');
  const [isCommentSheetOpen, setIsCommentSheetOpen] = useState(false);
  const [currentFeedId, setCurrentFeedId] = useState<string>();
  const itemsRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  // 상태 업데이트가 반영되기 전 스크롤 이벤트가 연달아 와도 다음 페이지를 한 번만 요청한다.
  const loadingRef = useRef(false);
  // 뒤로 가기 복원용 최신 값. 목록이 사라질 때 저장한다.
  const restorationRef = useRef({ beforeCreatedAt: undefined as string | undefined, page: 0, hasMore: true, scrollY: 0 });
  const lastFeedIdRef = useRef<string | undefined>(undefined);
  const restoredRef = useRef(false);
  const restoredKeyRef = useRef<string | undefined>(undefined);
  const pendingRestoreRef = useRef<{ scrollY: number; feedId?: string } | undefined>(undefined);
  const propsRef = useRef(props);
  propsRef.current = props;
  const { isLoading: isAuthLoading, me } = useAuth();
  // 서버 목록에 대한 개인화 재조회를 조합마다 한 번만 수행하기 위한 표식.
  const personalizedKeyRef = useRef<string | undefined>(undefined);
  // 요청마다 부여하는 일련번호. 최신 요청만 결과를 반영하고 표식을 조작한다.
  const personalizationGenerationRef = useRef(0);
  // 개인화 요청 시작 시점의 목록 스냅샷을 얻기 위해 최신 목록을 들고 있는다.
  // feeds state 커밋 후(아래 동기화 이펙트)와 feedsProp 교체 시점 양쪽에서 갱신한다.
  const feedsRef = useRef<FeedDto[]>(feedsProp);

  // 개인화 이펙트보다 먼저 선언해, 같은 커밋에서 스냅샷이 항상 최신 목록을 가리키게 한다.
  useEffect(() => {
    feedsRef.current = feeds;
  }, [feeds]);

  useFeedLikeEvent(setFeeds);
  useFeedCommentEvent(setFeeds);

  const loadMore = useCallback(async (isRetry = false) => {
    if (!hasMore || loadingRef.current) return;
    // 실패한 뒤에는 스크롤로 자동 재요청하지 않고 '다시 시도'를 기다린다.
    if (loadError && !isRetry) return;
    loadingRef.current = true;
    setIsLoading(true);
    setLoadError(false);
    try {
      const nextPage = infiniteScrollType === 'page' ? page + 1 : undefined;

      const newFeeds = await api.feed.search({
        beforeCreatedAt: infiniteScrollType === 'beforeCreatedAt' ? beforeCreatedAt : undefined,
        keyword,
        accountName,
        hashTag,
        location,
        distance,
        page: nextPage,
        size,
        link,
        sortType,
      });

      if (newFeeds.list.length !== 0) {
        setFeeds((prev) => {
          const combined = [...prev, ...newFeeds.list];
          const uniqueSids = new Set();
          return combined.filter((feed) => {
            if (!feed.sid) return true;
            if (uniqueSids.has(feed.sid)) return false;
            uniqueSids.add(feed.sid);
            return true;
          });
        });
        setBeforeCreatedAt(newFeeds.list[newFeeds.list.length - 1].createdAt);
        if (infiniteScrollType === 'page') {
          setPage(nextPage || 0);
        }
      }

      if (newFeeds.list.length !== size) {
        setHasMore(false);
      }
      if (newFeeds.list.length > 0 && feedsRef.current.length > 0) {
        setAnnouncement(`게시물 ${newFeeds.list.length}개를 더 불러왔어요`);
      }
      onTotalElementsChange?.call(null, newFeeds.list.length);
    } catch (e: any) {
      console.error(e.message);
      setLoadError(true);
    } finally {
      loadingRef.current = false;
      setIsLoading(false);
    }

  }, [...paramsArray, hasMore, loadError, beforeCreatedAt, page, onTotalElementsChange, infiniteScrollType]);

  // 뒤로 가기로 같은 목록에 돌아왔으면 떠날 때의 카드·커서를 되살린다. 아래 초기화·첫 요청보다 먼저 판정한다.
  // 서버 렌더와 첫 클라이언트 렌더를 맞추기 위해 state 초기값이 아니라 마운트 뒤에 적용한다.
  useEffect(() => {
    const snapshot = takeFeedListSnapshot(buildRestorationKey(propsRef.current));
    if (!snapshot || snapshot.feeds.length === 0) return;

    restoredRef.current = true;
    restoredKeyRef.current = buildRestorationKey(propsRef.current);
    feedsRef.current = snapshot.feeds;
    setFeeds(snapshot.feeds);
    setBeforeCreatedAt(snapshot.beforeCreatedAt);
    setPage(snapshot.page);
    setHasMore(snapshot.hasMore);
    pendingRestoreRef.current = { scrollY: snapshot.scrollY, feedId: snapshot.feedId };
  }, []);

  useAsyncEffect(async () => {
    if (restoredRef.current && restoredKeyRef.current === buildRestorationKey(propsRef.current)) return;
    if (feedsProp.length !== 0) {
      if (infiniteScrollType === 'beforeCreatedAt' && !beforeCreatedAt) {
        setBeforeCreatedAt(feedsProp[feedsProp.length - 1].createdAt);
      }
      if (infiniteScrollType === 'page' && page === (pageProp || 0)) {
        // Already initialized with pageProp
      }
      return;
    }
    await loadMore();
  }, paramsArray);

  useEffect(() => {
    // 복원한 목록은 서버 목록으로 덮지 않는다. 검색어 등 조회 조건이 바뀌면 평소처럼 초기화한다.
    if (restoredRef.current && restoredKeyRef.current === buildRestorationKey(propsRef.current)) return;
    restoredRef.current = false;
    setLoadError(false);
    const uniqueSids = new Set();
    const normalized = [...feedsProp].filter((feed) => {
      if (!feed.sid) return true;
      if (uniqueSids.has(feed.sid)) return false;
      uniqueSids.add(feed.sid);
      return true;
    });
    setFeeds(normalized);
    // 이 이펙트는 개인화 이펙트보다 먼저 선언돼 있다. 같은 flush의 개인화 이펙트가
    // 아직 커밋되지 않은 이전 목록을 시작 스냅샷으로 잡지 않도록 새 목록을 즉시 기록한다.
    feedsRef.current = normalized;
    // 이 이펙트가 위 useAsyncEffect가 세팅한 커서를 되돌리지 않도록,
    // 서버에서 받은 초기 피드가 있으면 그 마지막 항목의 createdAt으로 초기화한다.
    setBeforeCreatedAt(feedsProp.length !== 0 ? feedsProp[feedsProp.length - 1].createdAt : undefined);
    setPage(pageProp || 0);
    setHasMore(true);
  }, paramsArray);

  // 서버 렌더 목록(feedsProp)은 쿠키 없이 조회돼 isLike·owner.isFollowing 등이 익명 기준이다.
  // 로그인 상태에서는 mount 후 같은 파라미터로 첫 페이지만 비차단 재조회해 개인화·카운트 필드를 덮어쓴다.
  // 순서와 커서는 건드리지 않고, 실패하면 서버 목록을 그대로 둔다. 스켈레톤도 띄우지 않는다.
  const personalizationKey = JSON.stringify([
    feedsProp.map(feed => feed.sid),
    keyword,
    accountName,
    hashTag,
    location,
    distance,
    pageProp,
    size,
    link,
    sortType,
    me?.activeAccount?.name ?? me?.email,
  ]);

  useEffect(() => {
    if (feedsProp.length === 0) return;
    // 비로그인이거나 아직 로그인 판정 전이면 서버가 준 익명 값과 같으므로 재조회하지 않는다.
    if (isAuthLoading || !me) return;
    if (personalizedKeyRef.current === personalizationKey) return;
    personalizedKeyRef.current = personalizationKey;

    const generation = personalizationGenerationRef.current + 1;
    personalizationGenerationRef.current = generation;
    // 이 요청이 응답이나 실패를 실제로 처리했는지. cleanup이 표식을 되돌릴지 판단하는 데 쓴다.
    let isSettled = false;
    let isCancelled = false;

    // 요청 시작 시점의 상태. 응답이 늦게 도착했을 때 그 사이 사용자가 바꾼 필드를 되돌리지 않도록 기준으로 삼는다.
    const startedBySid = new Map(
      feedsRef.current.filter(feed => feed.sid).map(feed => [feed.sid, feed]),
    );

    api.feed.search({
      keyword,
      accountName,
      hashTag,
      location,
      distance,
      page: pageProp,
      size,
      link,
      sortType,
    }).then((response) => {
      // 취소됐거나 이미 더 새로운 요청이 시작됐으면 반영하지 않는다.
      if (isCancelled || personalizationGenerationRef.current !== generation) return;
      isSettled = true;

      const freshBySid = new Map(
        response.list.filter(feed => feed.sid).map(feed => [feed.sid, feed]),
      );

      setFeeds(prev => prev.map((feed) => {
        const fresh = feed.sid ? freshBySid.get(feed.sid) : undefined;
        const started = feed.sid ? startedBySid.get(feed.sid) : undefined;
        // 요청 시작 이후 추가된 항목(무한 스크롤 결과)은 이미 개인화된 값이므로 건드리지 않는다.
        if (!fresh || !started) return feed;

        // 요청 중에 사용자가 바꾼 필드는 덮지 않는다.
        // 목록 state를 바꾸는 사용자 동작 경로는 좋아요(use-feed-like-event)와
        // 댓글(use-feed-comment-event) 둘뿐이라 그 필드만 비교한다.
        // shareCount·viewCount는 목록 state를 바꾸는 이벤트가 없어 그대로 덮는다.
        const isLikeMutated = feed.isLike !== started.isLike || feed.likeCount !== started.likeCount;
        const isCommentMutated = feed.commentCount !== started.commentCount;
        const isFollowMutated = feed.owner?.isFollowing !== started.owner?.isFollowing;

        return {
          ...feed,
          isLike: isLikeMutated ? feed.isLike : fresh.isLike,
          likeCount: isLikeMutated ? feed.likeCount : fresh.likeCount,
          commentCount: isCommentMutated ? feed.commentCount : fresh.commentCount,
          shareCount: fresh.shareCount,
          viewCount: fresh.viewCount,
          owner: feed.owner && fresh.owner && !isFollowMutated
            ? { ...feed.owner, isFollowing: fresh.owner.isFollowing }
            : feed.owner,
        };
      }));
    }).catch((e: any) => {
      // 오래된 요청의 실패가 새 요청의 표식을 지우지 않게 한다.
      if (isCancelled || personalizationGenerationRef.current !== generation) return;
      isSettled = true;
      // 재조회 실패 시 서버 목록을 유지하고, 다음 setup이 다시 시도할 수 있게 표식을 되돌린다.
      personalizedKeyRef.current = undefined;
      console.error(e?.message ?? e);
    });

    return () => {
      isCancelled = true;
      // 아직 결과를 처리하지 못한 채 정리되면 표식을 되돌려, 다음 setup이 같은 키로 다시 요청하게 한다.
      // (인증 재조회로 isAuthLoading이 오갈 때와 StrictMode의 setup→cleanup→setup에서 필요하다.)
      if (!isSettled && personalizationGenerationRef.current === generation) {
        personalizedKeyRef.current = undefined;
      }
    };
  }, [personalizationKey, isAuthLoading, me]);

  useEffect(() => {
    const onScroll = () => {
      restorationRef.current.scrollY = window.scrollY;
      // masonry 컨테이너 높이가 가장 긴 열과 같으므로, 문서 하단 기준이 곧 가장 긴 열 하단 기준이다.
      const scrollHeight = document.body.scrollHeight;
      const scrollY = window.scrollY + window.innerHeight;
      if (scrollY < scrollHeight - (window.innerHeight * 2)) return;
      loadMore().then();
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, [loadMore]);

  useEffect(() => {
    restorationRef.current.beforeCreatedAt = beforeCreatedAt;
    restorationRef.current.page = page;
    restorationRef.current.hasMore = hasMore;
  }, [beforeCreatedAt, page, hasMore]);

  // 목록을 떠날 때(상세·프로필 등으로 이동) 상태를 저장한다. 되살리는 것은 뒤로 가기로 돌아왔을 때뿐이다.
  useEffect(() => {
    const key = buildRestorationKey(propsRef.current);
    return () => {
      const { beforeCreatedAt: cursor, page: currentPage, hasMore: more, scrollY } = restorationRef.current;
      if (feedsRef.current.length === 0) return;
      // 복원한 스크롤·초점을 아직 적용하기 전에 정리되면(개발 모드 StrictMode의 재마운트 등) 그 값을 그대로 넘긴다.
      const pending = pendingRestoreRef.current;
      saveFeedListSnapshot(key, {
        feeds: feedsRef.current,
        beforeCreatedAt: cursor,
        page: currentPage,
        hasMore: more,
        scrollY: pending?.scrollY ?? scrollY,
        feedId: pending?.feedId ?? lastFeedIdRef.current,
      });
    };
  }, []);

  // 복원한 카드가 그려지고 masonry 배치가 끝난 뒤 스크롤과 마지막으로 연 게시물의 초점을 되돌린다.
  useEffect(() => {
    const pending = pendingRestoreRef.current;
    if (!pending) return;

    let secondFrame: number | undefined;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        pendingRestoreRef.current = undefined;
        window.scrollTo(0, pending.scrollY);
        restorationRef.current.scrollY = pending.scrollY;
        if (!pending.feedId) return;
        const target = itemsRef.current?.querySelector<HTMLElement>(
          `[data-feed-id="${CSS.escape(pending.feedId)}"] [data-feed-link]`,
        );
        target?.focus({ preventScroll: true });
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame !== undefined) window.cancelAnimationFrame(secondFrame);
    };
  }, [feeds]);

  // 목록 안에서 마지막으로 누르거나 초점을 둔 게시물. 뒤로 가기 때 그 카드로 초점을 돌려준다.
  const rememberFeed = useCallback((event: React.SyntheticEvent<HTMLDivElement>) => {
    const item = (event.target as HTMLElement).closest<HTMLElement>('[data-feed-id]');
    if (item?.dataset.feedId) lastFeedIdRef.current = item.dataset.feedId;
  }, []);

  // PC는 목록에서 댓글 시트를 띄우지 않고 상세의 댓글 패널로 보낸다.
  const handleComment = useCallback((feedId?: string) => {
    if (!feedId) return;
    lastFeedIdRef.current = feedId;
    if (window.matchMedia(PC_MEDIA_QUERY).matches) {
      router.push(`/feeds/${feedId}#comments`);
      return;
    }
    setCurrentFeedId(feedId);
    setIsCommentSheetOpen(true);
  }, [router]);

  const handleDelete = useCallback((feedId: string, ownerName?: string) => {
    setFeeds((prevFeeds) => prevFeeds.filter((feed) => {
      if (ownerName) {
        return feed.owner?.name !== ownerName;
      }
      return feed.sid !== feedId;
    }));
  }, []);

  useEffect(() => {
    if (feedsProp.length !== 0) {
      setFeeds((prev) => {
        const uniqueSids = new Set();
        return prev.filter((feed) => {
          if (!feed.sid) return true;
          if (uniqueSids.has(feed.sid)) return false;
          uniqueSids.add(feed.sid);
          return true;
        });
      });
    }
  }, [feedsProp]);

  useEffect(() => {
    if (!isCommentSheetOpen) return;

    let animationFrame: number | undefined;
    const updateCurrentFeed = () => {
      animationFrame = undefined;
      const feedElements = itemsRef.current?.querySelectorAll<HTMLElement>('[data-feed-id]');
      if (!feedElements?.length) return;

      let largestVisibleArea = 0;
      let largestFeedId: string | undefined;
      let currentVisibleArea = 0;

      for (const element of feedElements) {
        const rect = element.getBoundingClientRect();
        const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
        const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
        const visibleArea = visibleWidth * visibleHeight;
        const feedId = element.dataset.feedId;

        if (feedId === currentFeedId) currentVisibleArea = visibleArea;
        if (visibleArea > largestVisibleArea) {
          largestVisibleArea = visibleArea;
          largestFeedId = feedId;
        }
      }

      if (largestFeedId && largestVisibleArea > currentVisibleArea) {
        setCurrentFeedId(largestFeedId);
      }
    };
    const scheduleUpdate = () => {
      if (animationFrame === undefined) {
        animationFrame = window.requestAnimationFrame(updateCurrentFeed);
      }
    };

    scheduleUpdate();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
    };
  }, [isCommentSheetOpen, currentFeedId]);

  return (
    <>
      <FeedMasonry
        ref={itemsRef}
        className={className}
        onClickCapture={rememberFeed}
        onFocusCapture={rememberFeed}
      >
        {feeds.map((feed, index) => (
          <FeedMasonryItem key={feed.sid ?? index} feedId={feed.sid}>
            <FeedItem
              {...feed}
              isFirst={index === 0}
              onComment={() => handleComment(feed.sid)}
              onShare={() => {
              }}
              onDelete={handleDelete}
            />
          </FeedMasonryItem>
        ))}

        {isLoading && Array.from({ length: LOADING_SKELETON_COUNT }).map((_, index) => (
          <FeedMasonryItem key={`skeleton-${index}`} aria-hidden="true">
            <SkeletonFeedItem />
          </FeedMasonryItem>
        ))}
      </FeedMasonry>

      {loadError && (
        <div className={styles.status} role="alert">
          <Typography className={styles.statusTitle} weight="bold">불러오지 못했어요</Typography>
          <Typography className={styles.statusText} size="small">연결 상태를 확인하고 다시 시도해 주세요.</Typography>
          <Button className={styles.retryButton} variant="secondary" onClick={() => loadMore(true)}>
            다시 시도
          </Button>
        </div>
      )}

      {!hasMore && !loadError && feeds.length > 0 && (
        <Typography className={`${styles.status} ${styles.endStatus}`} size="small" align="center">
          모든 게시물을 봤어요
        </Typography>
      )}

      <div className={styles.visuallyHidden} aria-live="polite" role="status">{announcement}</div>

      <CommentActionSheet
        isOpen={isCommentSheetOpen}
        onClose={() => setIsCommentSheetOpen(false)}
        feedId={currentFeedId}
      />

      {feeds.length === 0 && !isLoading && !loadError && onEmptyResult?.()}
    </>
  );
}
